import {
  getDateString,
  getMatchDurationFromConstraints,
  getTournamentTimezone,
  resolveTournamentDayForMatch,
} from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import { ClassFormatEnum } from '../../types/enums';
import type { Class } from '../../types/tournament';
import {
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../types/schedule';
import { flattenScheduledMatches, MS_PER_MINUTE } from './scheduleOverlap';
import {
  bracketOrderingShouldSkipPair,
  buildDePhaseIndexForClass,
} from './scheduleOrderingViolation';
import { schedulingOrderKey } from './scheduleDePhase';
import { getDisplayName } from '../../utils/userDisplayName';
import { mergeOverlapsForDisplay } from './scheduleConflictDisplay';
import {
  getMaxParentClassEndMs,
  getMinChildClassStartMs,
  hasChildBeforeParentConflict,
  hasParentAfterChildConflict,
  resolveParentClassId,
} from './scheduleParentClass';
import {
  boxRoundOrderingViolation,
  boxRoundPhaseStableId,
  buildBoxScopeBuckets,
  findBoxRoundPhaseViolation,
  getBoxScopeKey,
  normalizeBoxLeagueRound,
} from './scheduleBoxLeagueRounds';

export type ScheduleConflictKind =
  | 'PLAYER_OVERLAP'
  | 'TEAM_OVERLAP'
  | 'COURT_OVERLAP'
  | 'MISSING_END'
  | 'BRACKET_ORDERING'
  | 'BOX_ROUND_ORDERING'
  | 'PARENT_CLASS_ORDERING';

export type ScheduleConflict = {
  id: string;
  kind: ScheduleConflictKind;
  severity: 'error' | 'warning';
  entityId: number;
  entityLabel: string;
  matchIds: [number, number];
  dayKey: number;
  messageKey: string;
  messageParams: Record<string, string | number>;
};

export type MatchScheduleInterval = {
  match: IMatchScheduleItem;
  startMs: number;
  endMs: number;
  dayKey: number;
  courtId: number | null;
  missingEnd: boolean;
};

const DEFAULT_DURATION_MINUTES = 30;

export function getMatchScheduleInterval(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classObject?: Class | null
): MatchScheduleInterval | null {
  if (!match.scheduleDate) return null;

  const startMs = new Date(match.scheduleDate).getTime();
  if (!Number.isFinite(startMs)) return null;

  const durationFromConstraints = constraints
    ? getMatchDurationFromConstraints(match, constraints, classObject)
    : null;
  const durationMinutes = match.duration ?? durationFromConstraints;
  const hasExplicitEnd = Boolean(match.scheduleEndDate);
  const missingEnd =
    !hasExplicitEnd && (durationMinutes == null || !Number.isFinite(durationMinutes));

  let endMs = hasExplicitEnd
    ? new Date(match.scheduleEndDate!).getTime()
    : startMs + (durationMinutes ?? DEFAULT_DURATION_MINUTES) * MS_PER_MINUTE;

  if (
    hasExplicitEnd &&
    (!Number.isFinite(endMs) || endMs <= startMs) &&
    !missingEnd
  ) {
    console.warn('[schedule] invalid scheduleEndDate', {
      matchId: match.id,
      scheduleEndDate: match.scheduleEndDate,
    });
  }

  if (!Number.isFinite(endMs) || endMs <= startMs) {
    if (missingEnd) {
      endMs = startMs;
    } else {
      endMs = startMs + (durationMinutes ?? DEFAULT_DURATION_MINUTES) * MS_PER_MINUTE;
    }
  }

  const dayKey = resolveMatchDayKey(match.scheduleDate, constraints, timeZone, match);
  const courtId = match.court?.id ?? null;

  return {
    match,
    startMs,
    endMs,
    dayKey,
    courtId,
    missingEnd,
  };
}

export function resolveMatchDayKey(
  scheduleDate: string,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  match?: IMatchScheduleItem | null
): number {
  if (match?.court && constraints) {
    const tournamentDay = resolveTournamentDayForMatch(match, constraints, timeZone);
    if (tournamentDay) return tournamentDay.dayKey;
  }

  const dateStr = getDateString(scheduleDate, timeZone);
  const day = constraints?.days.find((d) => {
    const dayStr = getDateString(d.startTime, timeZone);
    return dayStr === dateStr;
  });
  return day?.dayKey ?? 0;
}

export function isBoxLeagueClass(classObject: Class | undefined): boolean {
  if (!classObject) return false;
  return (
    classObject.format === ClassFormatEnum.BOX_LEAGUE ||
    classObject.format === ClassFormatEnum.BOX_LEAGUE_ELIM
  );
}

export function isKnockoutStageMatch(match: IMatchScheduleItem): boolean {
  return !match.tournamentGroup;
}

export function isGroupElimClassFormat(classObject: Class | undefined): boolean {
  if (!classObject) return false;
  return (
    classObject.format === ClassFormatEnum.GROUP ||
    classObject.format === ClassFormatEnum.GROUP_BRACKET
  );
}

export function hasGroupElimPhaseViolation(
  a: MatchScheduleInterval,
  b: MatchScheduleInterval,
  classObject: Class | undefined
): boolean {
  if (!isGroupElimClassFormat(classObject)) return false;
  const aGroup = !isKnockoutStageMatch(a.match);
  const bGroup = !isKnockoutStageMatch(b.match);
  if (aGroup === bGroup) return false;
  const groupInterval = aGroup ? a : b;
  const elimInterval = aGroup ? b : a;
  return elimInterval.startMs < groupInterval.endMs;
}

export function schedulingRoundKey(
  match: IMatchScheduleItem,
  classObject: Class | undefined
): number | null {
  if (isBoxLeagueClass(classObject)) {
    const n = Number(match.type);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  }
  if (!isKnockoutStageMatch(match)) {
    return null;
  }
  const base = typeof match.type === 'number' ? match.type : 0;
  return base + (match.isGold ? 1 : 0);
}

function intervalsOverlap(a: MatchScheduleInterval, b: MatchScheduleInterval): boolean {
  if (a.dayKey !== b.dayKey) return false;
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

function stablePairId(kind: string, entityId: number, idA: number, idB: number): string {
  const [lo, hi] = idA < idB ? [idA, idB] : [idB, idA];
  return `${kind}-${entityId}-${lo}-${hi}`;
}

function stableMatchPairId(kind: string, idA: number, idB: number): string {
  const [lo, hi] = idA < idB ? [idA, idB] : [idB, idA];
  return `${kind}-pair-${lo}-${hi}`;
}

function playerLabel(match: IMatchScheduleItem, playerId: number): string {
  const players = [
    match.homePlayer1,
    match.homePlayer2,
    match.awayPlayer1,
    match.awayPlayer2,
  ];
  const found = players.find((p) => p?.id === playerId);
  return getDisplayName(found) || `Player ${playerId}`;
}

function teamLabel(match: IMatchScheduleItem, teamId: number): string {
  if (match.homeTeam?.id === teamId && match.homeTeam.name) return match.homeTeam.name;
  if (match.awayTeam?.id === teamId && match.awayTeam.name) return match.awayTeam.name;
  return `Team ${teamId}`;
}

function collectPlayerIds(match: IMatchScheduleItem): number[] {
  const ids: number[] = [];
  for (const p of [
    match.homePlayer1,
    match.homePlayer2,
    match.awayPlayer1,
    match.awayPlayer2,
  ]) {
    if (p?.id != null) ids.push(Number(p.id));
  }
  return ids;
}

function collectTeamIds(match: IMatchScheduleItem): number[] {
  const ids: number[] = [];
  if (match.homeTeam?.id != null) ids.push(Number(match.homeTeam.id));
  if (match.awayTeam?.id != null) ids.push(Number(match.awayTeam.id));
  return ids;
}

type EntityOverlapAccum = {
  intervalA: MatchScheduleInterval;
  intervalB: MatchScheduleInterval;
  entityIds: number[];
  entityLabels: string[];
};

function emitEntityOverlaps(
  kind: 'PLAYER_OVERLAP' | 'TEAM_OVERLAP',
  intervals: MatchScheduleInterval[],
  getEntityIds: (match: IMatchScheduleItem) => number[],
  getLabel: (match: IMatchScheduleItem, entityId: number) => string,
  singularMessageKey: string,
  pluralMessageKey: string,
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  const byEntity = new Map<number, MatchScheduleInterval[]>();

  for (const interval of intervals) {
    for (const entityId of getEntityIds(interval.match)) {
      const list = byEntity.get(entityId) ?? [];
      list.push(interval);
      byEntity.set(entityId, list);
    }
  }

  const pairMap = new Map<string, EntityOverlapAccum>();
  const labelsSeen = new Map<string, Set<string>>();

  for (const [entityId, list] of byEntity) {
    if (list.length < 2) continue;
    const sorted = [...list].sort((a, b) => a.startMs - b.startMs);
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i];
        const b = sorted[j];
        if (!intervalsOverlap(a, b)) continue;
        const pairId = stableMatchPairId(kind, a.match.id, b.match.id);
        let accum = pairMap.get(pairId);
        if (!accum) {
          accum = {
            intervalA: a,
            intervalB: b,
            entityIds: [],
            entityLabels: [],
          };
          pairMap.set(pairId, accum);
          labelsSeen.set(pairId, new Set());
        }
        const seenLabels = labelsSeen.get(pairId)!;
        const label = getLabel(a.match, entityId);
        if (seenLabels.has(label)) continue;
        seenLabels.add(label);
        accum.entityIds.push(entityId);
        accum.entityLabels.push(label);
      }
    }
  }

  for (const [id, accum] of pairMap) {
    if (seen.has(id)) continue;
    seen.add(id);
    const entityLabel = accum.entityLabels.join(', ');
    const messageKey =
      accum.entityLabels.length > 1 ? pluralMessageKey : singularMessageKey;
    out.push({
      id,
      kind,
      severity: 'error',
      entityId: accum.entityIds[0] ?? 0,
      entityLabel,
      matchIds: [accum.intervalA.match.id, accum.intervalB.match.id],
      dayKey: accum.intervalA.dayKey,
      messageKey,
      messageParams: {
        entityLabel,
        matchIdA: accum.intervalA.match.id,
        matchIdB: accum.intervalB.match.id,
        overlapStartMs: Math.min(accum.intervalA.startMs, accum.intervalB.startMs),
      },
    });
  }
}

function emitCourtOverlaps(
  intervals: MatchScheduleInterval[],
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  const byCourt = new Map<string, MatchScheduleInterval[]>();

  for (const interval of intervals) {
    if (interval.courtId == null) continue;
    const key = `${interval.dayKey}-${interval.courtId}`;
    const list = byCourt.get(key) ?? [];
    list.push(interval);
    byCourt.set(key, list);
  }

  for (const list of byCourt.values()) {
    if (list.length < 2) continue;
    const sorted = [...list].sort((a, b) => a.startMs - b.startMs);
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i];
        const b = sorted[j];
        if (!intervalsOverlap(a, b)) continue;
        const courtId = a.courtId!;
        const id = stablePairId('COURT_OVERLAP', courtId, a.match.id, b.match.id);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({
          id,
          kind: 'COURT_OVERLAP',
          severity: 'error',
          entityId: courtId,
          entityLabel: a.match.court?.name ?? `Court ${courtId}`,
          matchIds: [a.match.id, b.match.id],
          dayKey: a.dayKey,
          messageKey: 'scheduleWarningCourtOverlap',
          messageParams: {
            courtLabel: a.match.court?.name ?? `Court ${courtId}`,
            matchIdA: a.match.id,
            matchIdB: b.match.id,
            overlapStartMs: Math.min(a.startMs, b.startMs),
          },
        });
      }
    }
  }
}

function emitMissingEndWarnings(
  intervals: MatchScheduleInterval[],
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  for (const interval of intervals) {
    if (!interval.missingEnd) continue;
    const id = `MISSING_END-${interval.match.id}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      kind: 'MISSING_END',
      severity: 'warning',
      entityId: interval.match.id,
      entityLabel: interval.match.matchId || String(interval.match.id),
      matchIds: [interval.match.id, interval.match.id],
      dayKey: interval.dayKey,
      messageKey: 'scheduleWarningMissingEnd',
      messageParams: { matchId: interval.match.id },
    });
  }
}

function emitBracketOrderingConflicts(
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>,
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  const byClass = new Map<number, MatchScheduleInterval[]>();

  for (const interval of intervals) {
    const classId = interval.match.classId;
    const classObject = classIdToObject[String(classId)] ?? classIdToObject[classId];
    if (isBoxLeagueClass(classObject)) continue;
    const roundKey = schedulingRoundKey(interval.match, classObject);
    if (roundKey == null || roundKey <= 0) continue;
    const list = byClass.get(classId) ?? [];
    list.push(interval);
    byClass.set(classId, list);
  }

  for (const [, classIntervals] of byClass) {
    const classObject =
      classIdToObject[String(classIntervals[0]?.match.classId)] ??
      classIdToObject[classIntervals[0]?.match.classId];
    const dePhaseIndex = buildDePhaseIndexForClass(classIntervals, classObject);

    for (const later of classIntervals) {
      if (later.match.tournamentGroup?.id) continue;
      const laterKey = schedulingOrderKey(
        later.match,
        classObject,
        dePhaseIndex,
        classIntervals
      );
      if (laterKey == null) continue;

      for (const earlier of classIntervals) {
        if (earlier.match.id === later.match.id) continue;
        if (earlier.match.tournamentGroup?.id) continue;
        const earlierKey = schedulingOrderKey(
          earlier.match,
          classObject,
          dePhaseIndex,
          classIntervals
        );
        if (earlierKey == null || earlierKey >= laterKey) continue;
        if (earlierKey === laterKey) continue;
        if (bracketOrderingShouldSkipPair(earlier, later)) continue;
        if (later.startMs >= earlier.endMs) continue;

        const id = stablePairId(
          'BRACKET_ORDERING',
          later.match.classId,
          earlier.match.id,
          later.match.id
        );
        if (seen.has(id)) continue;
        seen.add(id);

        out.push({
          id,
          kind: 'BRACKET_ORDERING',
          severity: 'error',
          entityId: later.match.classId,
          entityLabel: classObject?.name ?? `Class ${later.match.classId}`,
          matchIds: [earlier.match.id, later.match.id],
          dayKey: later.dayKey,
          messageKey: 'scheduleWarningBracketOrdering',
          messageParams: {
            classLabel: classObject?.name ?? String(later.match.classId),
            prerequisiteMatchId: earlier.match.id,
            matchId: later.match.id,
          },
        });
      }
    }
  }
}

function emitGroupElimPhaseConflicts(
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>,
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  const byClass = new Map<number, MatchScheduleInterval[]>();

  for (const interval of intervals) {
    const classId = interval.match.classId;
    const classObject = classIdToObject[String(classId)] ?? classIdToObject[classId];
    if (!isGroupElimClassFormat(classObject)) continue;
    const list = byClass.get(classId) ?? [];
    list.push(interval);
    byClass.set(classId, list);
  }

  for (const [, classIntervals] of byClass) {
    const classObject =
      classIdToObject[String(classIntervals[0]?.match.classId)] ??
      classIdToObject[classIntervals[0]?.match.classId];

    for (const elim of classIntervals) {
      if (!isKnockoutStageMatch(elim.match)) continue;
      for (const group of classIntervals) {
        if (isKnockoutStageMatch(group.match)) continue;
        if (group.match.id === elim.match.id) continue;
        if (elim.startMs >= group.endMs) continue;

        const id = stablePairId(
          'BRACKET_ORDERING',
          elim.match.classId,
          group.match.id,
          elim.match.id
        );
        if (seen.has(id)) continue;
        seen.add(id);

        out.push({
          id,
          kind: 'BRACKET_ORDERING',
          severity: 'error',
          entityId: elim.match.classId,
          entityLabel: classObject?.name ?? `Class ${elim.match.classId}`,
          matchIds: [group.match.id, elim.match.id],
          dayKey: elim.dayKey,
          messageKey: 'scheduleWarningBracketOrdering',
          messageParams: {
            classLabel: classObject?.name ?? String(elim.match.classId),
            prerequisiteMatchId: group.match.id,
            matchId: elim.match.id,
          },
        });
      }
    }
  }
}

function emitParentClassOrderingConflicts(
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>,
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  for (const childInterval of intervals) {
    const childClassId = childInterval.match.classId;
    if (resolveParentClassId(childClassId, classIdToObject) == null) continue;
    if (!hasChildBeforeParentConflict(childInterval, intervals, classIdToObject)) continue;

    const parentInfo = getMaxParentClassEndMs(childClassId, intervals, classIdToObject);
    if (!parentInfo) continue;

    const childClass =
      classIdToObject[String(childClassId)] ?? classIdToObject[childClassId];
    const parentClassId = childClass?.parentClassId;
    const parentClass =
      parentClassId != null
        ? classIdToObject[String(parentClassId)] ?? classIdToObject[parentClassId]
        : undefined;

    const id = stablePairId(
      'PARENT_CLASS_ORDERING-child',
      childClassId,
      parentInfo.latestMatchId,
      childInterval.match.id
    );
    if (seen.has(id)) continue;
    seen.add(id);

    out.push({
      id,
      kind: 'PARENT_CLASS_ORDERING',
      severity: 'error',
      entityId: childClassId,
      entityLabel: childClass?.name ?? `Class ${childClassId}`,
      matchIds: [parentInfo.latestMatchId, childInterval.match.id],
      dayKey: childInterval.dayKey,
      messageKey: 'scheduleWarningParentClassOrdering',
      messageParams: {
        direction: 'child_before_parent',
        childClassLabel: childClass?.name ?? String(childClassId),
        parentClassLabel: parentClass?.name ?? String(parentClassId),
        prerequisiteMatchId: parentInfo.latestMatchId,
        matchId: childInterval.match.id,
      },
    });
  }

  for (const parentInterval of intervals) {
    const parentClassId = parentInterval.match.classId;
    if (resolveParentClassId(parentClassId, classIdToObject) != null) continue;
    if (!hasParentAfterChildConflict(parentInterval, intervals, classIdToObject)) continue;

    const childInfo = getMinChildClassStartMs(parentClassId, intervals, classIdToObject);
    if (!childInfo) continue;

    const parentClass =
      classIdToObject[String(parentClassId)] ?? classIdToObject[parentClassId];
    const earliestChildInterval = intervals.find(
      (i) => i.match.id === childInfo.earliestMatchId
    );
    const earliestChildClassId = earliestChildInterval?.match.classId;
    const childClass =
      earliestChildClassId != null
        ? classIdToObject[String(earliestChildClassId)] ??
          classIdToObject[earliestChildClassId]
        : undefined;

    const id = stablePairId(
      'PARENT_CLASS_ORDERING-parent',
      parentClassId,
      parentInterval.match.id,
      childInfo.earliestMatchId
    );
    if (seen.has(id)) continue;
    seen.add(id);

    out.push({
      id,
      kind: 'PARENT_CLASS_ORDERING',
      severity: 'error',
      entityId: parentClassId,
      entityLabel: parentClass?.name ?? `Class ${parentClassId}`,
      matchIds: [parentInterval.match.id, childInfo.earliestMatchId],
      dayKey: parentInterval.dayKey,
      messageKey: 'scheduleWarningParentClassParentAfterChild',
      messageParams: {
        direction: 'parent_after_child',
        parentClassLabel: parentClass?.name ?? String(parentClassId),
        childClassLabel:
          childClass?.name ?? String(earliestChildClassId ?? 'Child class'),
        prerequisiteMatchId: childInfo.earliestMatchId,
        matchId: parentInterval.match.id,
      },
    });
  }
}

function pushBoxRoundConflict(
  out: ScheduleConflict[],
  seen: Set<string>,
  id: string,
  classObject: Class | undefined,
  classId: number,
  groupId: number,
  groupLabel: string,
  rLow: number,
  rHigh: number,
  earlierMatchId: number,
  laterMatchId: number,
  dayKey: number
): void {
  if (seen.has(id)) return;
  seen.add(id);
  out.push({
    id,
    kind: 'BOX_ROUND_ORDERING',
    severity: 'error',
    entityId: classId,
    entityLabel: classObject?.name ?? `Class ${classId}`,
    matchIds: [earlierMatchId, laterMatchId],
    dayKey,
    messageKey: 'scheduleWarningBoxRoundOrdering',
    messageParams: {
      classLabel: classObject?.name ?? String(classId),
      round: rHigh,
      rLow,
      rHigh,
      groupId,
      groupLabel,
      scope: groupId > 0 ? 'group' : 'class',
      prerequisiteMatchId: earlierMatchId,
      matchId: laterMatchId,
    },
  });
}

function emitBoxRoundOrderingForBucket(
  scopeKey: string,
  bucket: MatchScheduleInterval[],
  classObject: Class,
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  const phase = findBoxRoundPhaseViolation(bucket, classObject);
  if (phase) {
    const witness =
      bucket.find((i) => i.match.id === phase.laterMatchId) ??
      bucket.find((i) => i.match.id === phase.earlierMatchId);
    const groupLabel =
      witness?.match.tournamentGroup?.name ??
      (phase.groupId > 0 ? String(phase.groupId) : classObject.name);
    const phaseId = boxRoundPhaseStableId(scopeKey, phase.rLow, phase.rHigh);
    const dayKey =
      bucket.find((i) => i.match.id === phase.laterMatchId)?.dayKey ?? bucket[0]?.dayKey ?? 0;
    pushBoxRoundConflict(
      out,
      seen,
      phaseId,
      classObject,
      phase.classId,
      phase.groupId,
      groupLabel,
      phase.rLow,
      phase.rHigh,
      phase.earlierMatchId,
      phase.laterMatchId,
      dayKey
    );
  }

  for (let i = 0; i < bucket.length; i++) {
    for (let j = i + 1; j < bucket.length; j++) {
      const a = bucket[i];
      const b = bucket[j];
      if (!boxRoundOrderingViolation(a, b, classObject)) continue;

      const rA = normalizeBoxLeagueRound(a.match)!;
      const rB = normalizeBoxLeagueRound(b.match)!;
      const [earlier, later] = rA < rB ? [a, b] : [b, a];
      const rLow = Math.min(rA, rB);
      const rHigh = Math.max(rA, rB);
      const pairScopeKey = getBoxScopeKey(earlier, classObject) ?? scopeKey;
      const phaseId = boxRoundPhaseStableId(pairScopeKey, rLow, rHigh);
      if (seen.has(phaseId)) continue;

      const id = stablePairId(
        'BOX_ROUND_ORDERING',
        later.match.classId,
        earlier.match.id,
        later.match.id
      );
      const groupId = later.match.tournamentGroup?.id ?? 0;
      const groupLabel =
        later.match.tournamentGroup?.name ?? (groupId > 0 ? String(groupId) : classObject.name);
      pushBoxRoundConflict(
        out,
        seen,
        id,
        classObject,
        later.match.classId,
        groupId,
        groupLabel,
        rLow,
        rHigh,
        earlier.match.id,
        later.match.id,
        later.dayKey
      );
    }
  }
}

function emitBoxRoundOrderingConflicts(
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>,
  out: ScheduleConflict[],
  seen: Set<string>
): void {
  const buckets = buildBoxScopeBuckets(intervals, classIdToObject);

  const moveBoxEmitted = new Set<number>();
  for (const [scopeKey, { bucket, classObject }] of buckets) {
    if (classObject.moveBox) {
      const classId = bucket[0]?.match.classId;
      if (classId == null || moveBoxEmitted.has(classId)) continue;
      moveBoxEmitted.add(classId);
      const classIntervals = intervals.filter((i) => i.match.classId === classId);
      emitBoxRoundOrderingForBucket(
        `${classId}:class`,
        classIntervals,
        classObject,
        out,
        seen
      );
      continue;
    }
    emitBoxRoundOrderingForBucket(scopeKey, bucket, classObject, out, seen);
  }
}

const SEVERITY_ORDER: Record<ScheduleConflictKind, number> = {
  PLAYER_OVERLAP: 0,
  TEAM_OVERLAP: 0,
  COURT_OVERLAP: 0,
  BRACKET_ORDERING: 0,
  BOX_ROUND_ORDERING: 0,
  PARENT_CLASS_ORDERING: 0,
  MISSING_END: 1,
};

export function detectScheduleConflicts(
  schedule: ITournamentSchedule | null | undefined,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classIdToObject: Record<string, Class> = {},
  options?: { dayKey?: number }
): ScheduleConflict[] {
  const matches = flattenScheduledMatches(schedule);
  if (matches.length === 0) return [];

  const intervals: MatchScheduleInterval[] = [];
  for (const match of matches) {
    const classObject =
      classIdToObject[String(match.classId)] ?? classIdToObject[match.classId];
    const interval = getMatchScheduleInterval(match, constraints, timeZone, classObject);
    if (!interval) continue;
    if (options?.dayKey != null && interval.dayKey !== options.dayKey) {
      continue;
    }
    intervals.push(interval);
  }

  const out: ScheduleConflict[] = [];
  const seen = new Set<string>();

  emitEntityOverlaps(
    'PLAYER_OVERLAP',
    intervals,
    collectPlayerIds,
    playerLabel,
    'scheduleWarningPlayerOverlap',
    'scheduleWarningPlayerOverlapMultiple',
    out,
    seen
  );
  emitEntityOverlaps(
    'TEAM_OVERLAP',
    intervals,
    collectTeamIds,
    teamLabel,
    'scheduleWarningTeamOverlap',
    'scheduleWarningTeamOverlapMultiple',
    out,
    seen
  );
  emitCourtOverlaps(intervals, out, seen);
  emitMissingEndWarnings(intervals, out, seen);
  emitBracketOrderingConflicts(intervals, classIdToObject, out, seen);
  emitGroupElimPhaseConflicts(intervals, classIdToObject, out, seen);
  emitParentClassOrderingConflicts(intervals, classIdToObject, out, seen);
  emitBoxRoundOrderingConflicts(intervals, classIdToObject, out, seen);

  const merged: ScheduleConflict[] = mergeOverlapsForDisplay(out);

  merged.sort((a: ScheduleConflict, b: ScheduleConflict) => {
    const sev =
      (a.severity === 'error' ? 0 : 1) - (b.severity === 'error' ? 0 : 1);
    if (sev !== 0) return sev;
    if (a.dayKey !== b.dayKey) return a.dayKey - b.dayKey;
    const kindA = SEVERITY_ORDER[a.kind] ?? 0;
    const kindB = SEVERITY_ORDER[b.kind] ?? 0;
    if (kindA !== kindB) return kindA - kindB;
    return a.entityLabel.localeCompare(b.entityLabel);
  });

  return merged;
}

export function getScheduleConflictMatchIds(conflicts: ScheduleConflict[]): Set<number> {
  const ids = new Set<number>();
  for (const conflict of conflicts) {
    for (const matchId of conflict.matchIds) {
      ids.add(matchId);
    }
  }
  return ids;
}

export { getTournamentTimezone };
